// A dated ladder of what this athlete has achieved -- not tasks. No checkbox,
// no due date, never overdue (spec §4). Unachieved catalog milestones stay
// visible so the whole ladder reads at a glance.
import React, { useState } from 'react';
import { fmtDay } from '../../lib/conciergeTodoModel';

function RecordForm({ templateKey, onCancel, onRecord, saving }) {
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const custom = templateKey === null;
  const ready = !!when && (!custom || !!title.trim());
  return (
    <form className="cn-ms__form" data-testid="ms-record-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || saving) return;
      onRecord({ template_key: templateKey, title: custom ? title.trim() : null,
                 achieved_at: when, note: note.trim() || null });
    }}>
      {custom && (
        <input className="ae-field__input" aria-label="Milestone" value={title}
               placeholder="e.g. Named all-state" onChange={e => setTitle(e.target.value)} />
      )}
      <input className="ae-field__input" type="date" aria-label="Date it happened"
             value={when} onChange={e => setWhen(e.target.value)} />
      <input className="ae-field__input" aria-label="Note" value={note} placeholder="Note (optional)"
             onChange={e => setNote(e.target.value)} />
      <button type="submit" className="cn-btn" disabled={!ready || saving}>Record</button>
      <button type="button" className="cn-todo__action" onClick={onCancel}>Cancel</button>
    </form>
  );
}

// `recording` tracks which inline form is open: undefined = none, a template
// key = that catalog row's form, or the CUSTOM_FORM sentinel = the "+ Record a
// milestone" form at the bottom. A custom (already-recorded) milestone also
// has template_key === null, so comparing `recording` straight against
// `m.template_key` would match that row too once the custom form was open --
// it stays hidden today only because a recorded milestone always has
// achieved_at set. The sentinel keeps the two states from ever aliasing so
// that safety isn't load-bearing.
const CUSTOM_FORM = Symbol('custom-milestone-form');

export default function MilestoneTimeline({ milestones, readOnly, saving, onRecord, onRemove }) {
  const [recording, setRecording] = useState(undefined);   // template key, or CUSTOM_FORM
  const close = () => setRecording(undefined);
  const record = (body) => { onRecord(body); close(); };

  return (
    <div className="cn-todo-group" data-testid="todo-group-milestones">
      <h4 className="cn-todo-group__title">Milestones</h4>
      <ul className="cn-ms-list">
        {milestones.map(m => (
          <li key={m.template_key || `c${m.id}`}
              data-testid={`milestone-${m.template_key || m.id}`}
              className={`cn-ms${m.achieved_at ? ' cn-ms--done' : ' cn-ms--pending'}`}>
            <span className="cn-ms__dot" aria-hidden="true">{m.achieved_at ? '●' : '○'}</span>
            <span className="cn-ms__when">{m.achieved_at ? fmtDay(m.achieved_at) : ''}</span>
            <span className="cn-ms__body">
              <span className="cn-ms__title">{m.title}</span>
              {m.note && <span className="cn-ms__note">{m.note}</span>}
            </span>
            {!readOnly && (m.achieved_at
              ? <button type="button" className="cn-ms__action" disabled={saving}
                        aria-label={`Un-record ${m.title}`}
                        onClick={() => onRemove(m)}>Undo</button>
              : <button type="button" className="cn-ms__action" disabled={saving}
                        aria-label={`Record ${m.title}`}
                        onClick={() => setRecording(m.template_key)}>Record</button>)}
            {recording !== undefined && recording === m.template_key && !m.achieved_at && (
              <RecordForm templateKey={m.template_key} saving={saving}
                          onCancel={close} onRecord={record} />
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (recording === CUSTOM_FORM
        ? <RecordForm templateKey={null} saving={saving} onCancel={close} onRecord={record} />
        : <button type="button" className="cn-todo__action"
                  onClick={() => setRecording(CUSTOM_FORM)}>+ Record a milestone</button>)}
    </div>
  );
}
